<ng-template #valueCell let-field="field" let-value="value">
    @if (!value) {
        <span class="text-muted">Blank</span>
    } @else if (badgeClass(field, value); as badge) {
        <span class="badge" [ngClass]="badge">{{ displayValue(field, value) }}</span>
    } @else {
        <span>{{ displayValue(field, value) }}</span>
    }
</ng-template>

<div class="position-relative overflow-hidden h-100 d-flex">
    <div class="flex-grow-1 overflow-hidden px-sm-4" [class.pt-4]="!embedded">
        <dx-data-grid
            [style]="(`height: calc(100vh - ${offset}px)`)"
            id="userHistoryGrid"
            class="grid theme-dependent"
            noDataText=""
            [dataSource]="dataSource"
            [allowColumnResizing]="true"
            [focusedRowEnabled]="true"
            [autoNavigateToFocusedRow]="false"
            [showBorders]="true"
            [remoteOperations]="true"
            [columnAutoWidth]="true"
            [wordWrapEnabled]="true"
            [stateStoring]="{ enabled: true, type: 'localStorage', storageKey: 'user-history-grid-state', savingTimeout: 0 }"
            (onExporting)="onExporting($event)"
            [customizeColumns]="customizeColumns"
            keyExpr="id"
            width="100%"
        >
            <dxo-scrolling [scrollByContent]="true"></dxo-scrolling>
            <dxo-paging [pageSize]="10"></dxo-paging>
            <dxo-pager [visible]="true" [displayMode]="'full'" [showPageSizeSelector]="true" [showInfo]="true" [showNavigationButtons]="true"></dxo-pager>
            <dxo-load-panel [showPane]="false"></dxo-load-panel>
            <dxo-sorting mode="multiple"></dxo-sorting>
            <dxo-header-filter [visible]="true"></dxo-header-filter>
            <dxo-filter-row [visible]="true" [applyFilter]="'auto'"></dxo-filter-row>
            <dxo-column-chooser height="340px" [enabled]="true" [mode]="'select'">
                <dxo-position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button"></dxo-position>
                <dxo-search [enabled]="true"></dxo-search>
                <dxo-selection [allowSelectAll]="true" [selectByClick]="true" [recursive]="true"></dxo-selection>
            </dxo-column-chooser>
            <dxo-export [enabled]="true" [formats]="['xlsx']"></dxo-export>

            <dxo-toolbar>
                <dxi-item location="before">
                    <div class="grid-header">User History</div>
                </dxi-item>
                <dxi-item location="after" locateInMenu="auto" widget="dxButton"
                    [options]="{ text: 'Filters', icon: 'fa-sharp fa-thin fa-filter', onClick: toggleFilters, stylingMode: 'text' }"></dxi-item>
                <dxi-item location="after" locateInMenu="auto">
                    <div class="separator"></div>
                </dxi-item>
                <dxi-item location="after" locateInMenu="auto" showText="inMenu" widget="dxButton"
                    [options]="{ text: 'Clear Filters', icon: 'fa-sharp fa-thin fa-filter-slash', onClick: clearFilters, stylingMode: 'text' }"></dxi-item>
                <dxi-item location="after" locateInMenu="auto" showText="inMenu" widget="dxButton"
                    [options]="{ text: 'Reset', icon: 'restore', onClick: resetGrid, stylingMode: 'text' }"></dxi-item>
                <dxi-item location="after" locateInMenu="auto">
                    <div class="separator"></div>
                </dxi-item>
                <dxi-item name="columnChooserButton" locateInMenu="auto"></dxi-item>
                <dxi-item name="exportButton"></dxi-item>
            </dxo-toolbar>

            <dxi-column [dataField]="'employee.name'" caption="User" cellTemplate="employee" [minWidth]="250"></dxi-column>
            <dxi-column [dataField]="gridFieldName('created')" dataType="date" caption="Date" [minWidth]="180" cellTemplate="created" [sortOrder]="'desc'" selectedFilterOperation=">="></dxi-column>
            <dxi-column [dataField]="gridFieldName('section')" caption="Section" [minWidth]="180" [lookup]="{ dataSource: sectionOptions, valueExpr: 'value', displayExpr: 'text' }"></dxi-column>
            <dxi-column [dataField]="gridFieldName('field')" caption="Change" [minWidth]="320" cellTemplate="change" [calculateDisplayValue]="calculateChangeCellValue" [allowSorting]="false" [headerFilter]="{ dataSource: fieldOptions }" [lookup]="{ dataSource: fieldOptions, valueExpr: 'value', displayExpr: 'text' }"></dxi-column>

            <div *dxTemplate="let cellInfo of 'created'">
                {{ getCellData(cellInfo).created | date: dateTimeFormat }}
            </div>
            <div *dxTemplate="let cellInfo of 'employee'">
                @if (getCellData(cellInfo).employee; as employee) {
                    <user-avatar-email
                        [email]="employee.email"
                        [name]="employee.name"
                        [avatarUrl]="employee.avatarUrl"
                        [employeeId]="employee.id">
                    </user-avatar-email>
                }
            </div>
            <div *dxTemplate="let cellInfo of 'change'">
                <div class="d-flex align-items-center flex-wrap gap-1">
                    @if (getCellData(cellInfo); as row) {
                        <span class="fw-medium">{{ fieldLabel(row.field) }}:</span>
                        <ng-container *ngTemplateOutlet="valueCell; context: { field: row.field, value: row.oldValue }"></ng-container>
                        <span class="mx-1">→</span>
                        <ng-container *ngTemplateOutlet="valueCell; context: { field: row.field, value: row.newValue }"></ng-container>
                    }
                </div>
            </div>
        </dx-data-grid>
    </div>

    <filter-panel [(isOpened)]="isFilterPanelOpened" (onSearch)="applyFilters()" (onClear)="clearFilters()"
        (pinnedChange)="onPinnedChange()">
        <dx-form [formData]="filterModel">
            <dxi-item [dataField]="'employee.name'" editorType="dxTextBox" [editorOptions]="{ placeholder: 'User', stylingMode: 'filled' }"></dxi-item>
            <dxi-item [dataField]="filterModelName('section')" editorType="dxSelectBox" [editorOptions]="{ placeholder: 'Section', stylingMode: 'filled', items: sectionOptions, valueExpr: 'value', displayExpr: 'text', showClearButton: true }"></dxi-item>
            <dxi-item [dataField]="filterModelName('field')" editorType="dxSelectBox" [editorOptions]="{ placeholder: 'Changed Field', stylingMode: 'filled', items: fieldOptions, valueExpr: 'value', displayExpr: 'text', showClearButton: true }"></dxi-item>
            <dxi-item [dataField]="filterModelName('created')" editorType="dxDateBox" [editorOptions]="{ placeholder: 'Date', stylingMode: 'filled', displayFormat: 'shortDate' }"></dxi-item>
        </dx-form>
    </filter-panel>
</div>
